/* eslint-disable no-param-reassign */
/* eslint-disable no-tabs */
import axios from "axios";
import { backEndBaseURL } from "../constants/settings";
import { isWeb } from "./platform";
import useStores from "../hooks/use-stores";
import { navigate } from "gatsby";
import { GetUserProfile, IsAuthed } from "./general";
import { t } from "i18next";
import { GetMiniUrl } from "./mini";
import { SetLocalStorage } from "./cryptography";
import { USER_INFO } from "../constants/storage-keys";

// See network.ts's own copy of this — same "don't toast the same burst of
// concurrent 403/600s more than once" reasoning, kept separate per Axios instance.
let last403NotifyAt = 0;
let last600NotifyAt = 0;
const ERROR_NOTIFY_DEDUP_MS = 3000;

export const FAxios = axios.create({
  validateStatus(status: number) {
    return status >= 200 && status < 400;
  },
  baseURL: backEndBaseURL,
  // timeout: 5000,
});

FAxios.interceptors.request.use(
  // request 拦截器
  async (config: any) => {
    // See network.ts's Axios request interceptor for the same temp-token
    // scoping (clinic/info.tsx, clinic/products/index.tsx file uploads —
    // e.g. clinic logo/insurance docs — must also use the scoped token).
    const { userStore } = useStores();
    let token = "";
    if (userStore.isTempPermissionActive && userStore.tempToken) {
      token = userStore.tempToken;
    } else {
      // if (isWeb) {
      token = localStorage.USER_TOKEN;
      // }
    }
    config.headers.authorization = token;
    // config.headers["Content-Type"] = "multipart/form-data"; // To avoid OPTION request
    config.headers["ngrok-skip-browser-warning"] = "any";
    // console.log('[Network Request]', config.baseURL + config.url);
    return config;
  }, // 在发送请求时执行函数,可用来给 headers 携带 token
  (error: any) => {
    console.error("axios.request.error", error);
    throw error;
  }
);

FAxios.interceptors.response.use(
  // response 拦截器
  (response: any) => {
    if (process.env.NODE_ENV !== "production") {
      console.log(
        response.config.baseURL + response.config.url,
        "status:",
        response.status
      );
    }
    return response;
  }, // validateStatus 返回 true 时执行
  /* response = {
    data: {} || "", // `data` 给服务器发送请求的响应数据信息
    status: 200, // `status` 给服务器发送请求的响应 HTTP 状态码
    statusText: "OK", // `statusText` 给服务器发送请求的响应 HTTP 状态信息
    headers: {}, // `headers` 给服务器发送请求的响应 HTTP 响应头
    config: {}, // `config` 给服务器发送请求的配置信息
    request: {}, // `request` 给服务器发送请求的请求信息
  }; */
  // const { headers } = response || {};
  // const token = (headers || {})[AUTH_TOKEN_KEY];
  // setStore(AUTH_TOKEN_KEY, token);
  (error) => {
    // validateStatus 返回 false 时执行
    /* error = {
      message: "", // `message` 给服务器发送请求的响应错误标题
      response: {}, // `headers` 给服务器发送请求的响应信息
      request: {}, // `request` 给服务器发送请求的请求信息
      config: {}, // `config` 给服务器发送请求的配置信息
    }; */
    // console.log({error});
    try {
      const status = error.response?.status;
      const { userStore, packageStore, clinicStore, rootStore } = useStores();
      // See network.ts's response interceptor for why: a 401/402/403/600 while
      // using a clinic-scoped temp token (UserStore.isTempPermissionActive)
      // almost certainly reflects the temp clinic, not the real session — it
      // must not force-logout the user, navigate them away from this page, or
      // mutate the real session's currentClinicId/availableClinics.
      if (userStore.isTempPermissionActive && (status === 401 || status === 402 || status === 403 || status === 600)) {
        userStore.exitTempPermissionMode();
        return Promise.reject(error);
      }
      if (status === 401) {
        console.log("(/ω＼) unauthorised...", error);
        if (IsAuthed()) {
          rootStore.notify(t("AUTHENTICATION_EXPIRED"));
        }
        userStore.isAuthed = false;
        navigate("/");
      }
      if (status === 402) {
        const isMini = isWeb && window.location.pathname.startsWith("/mini");
        console.log("Need Payment...", error);
        packageStore.getMemberPackagePermission();
        rootStore.notify(t("VIP_PACKAGE_EXPIRED"), "error");
        navigate(!isMini ? "/clinic" : GetMiniUrl("/clinic"));
      }
      // See network.ts's response interceptor for the full reasoning — same
      // "insufficient permission" cleanup: silently refresh the RBAC cache,
      // then navigate back to the clinic home page since the current page is
      // most likely the one that just lost this permission.
      if (status === 403) {
        clinicStore.getMyPermissions().catch(() => { });
        const isMini = isWeb && window.location.pathname.startsWith("/mini");
        navigate(!isMini ? "/clinic" : GetMiniUrl("/clinic"));
        const now = Date.now();
        if (now - last403NotifyAt < ERROR_NOTIFY_DEDUP_MS) (error as any).suppressNotify = true;
        else last403NotifyAt = now;
      }
      // See network.ts's response interceptor for the full reasoning — same
      // "clinic relationship gone" cleanup, triggered here too since a file
      // upload (e.g. clinic logo/insurance docs) can just as easily 600.
      if (status === 600) {
        const userInfo = GetUserProfile();
        const currentClinicId = userInfo?.currentClinicId;
        if (userInfo && currentClinicId) {
          const remainingClinics = (userInfo.availableClinics ?? []).filter((c: any) => c.id != currentClinicId);
          userInfo.availableClinics = remainingClinics;
          if (remainingClinics.length > 0) {
            const nextClinicId = remainingClinics[0].id;
            userInfo.currentClinicId = nextClinicId;
            SetLocalStorage(USER_INFO, userInfo);
            userStore.selectClinic(nextClinicId).catch(() => { });
          } else {
            userInfo.currentClinicId = 0;
            SetLocalStorage(USER_INFO, userInfo);
          }
          if (isWeb && window.location.pathname.startsWith("/clinic/info")) {
            navigate("/clinic");
          }
        }
        const now = Date.now();
        if (now - last600NotifyAt < ERROR_NOTIFY_DEDUP_MS) (error as any).suppressNotify = true;
        else last600NotifyAt = now;
      }
    } catch {
      const errMsg =
        "Network error! Check the connection first. Then check the baseURL or Axios global request config." +
        "\n[RN] Make sure you're using ngrok or other non-localhost backends." +
        "\n[RN] For Conveyor extension, don't use https. Use http only.";
      throw new Error(errMsg);
    }
    return Promise.reject(error);
  }
);
